import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import moment from "moment";
import DataTable from "react-data-table-component";
import { NotificationManager } from "react-notifications";
import { makeStyles } from "@mui/styles";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";
import { putMarketMeters } from "services/ConfigServices";
import { metersStatusData, energyData } from "variables/lists/markets";
import useMetersTable from "./useMetersTable.js";
import EditMeterDialog from "./EditMeterDialog";
import CustomDeleteButton from "components/CustomDeleteButton/CustomDeleteButton.js";
import UserService from "services/UserService";
import Button from "components/CustomButtons/Button.js";
import styled from "styled-components";
import AdvancedSearchContext from "context/AdvancedSearchContext";
import { memberships } from "variables/markets";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import { withStyles } from "@mui/styles";
import MuiDialogContent from "@mui/material/DialogContent";
import MuiDialogActions from "@mui/material/DialogActions";
import CustomDialogTitle from "components/Dialog/CustomDialogTitle.js";
import MarketsContext from "context/MarketsContext";


const GroupCell = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Container = styled.div`
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.15);
  border-top: 4px solid rgba(255, 255, 255, 1);
  background-color: white;
  margin-top: -0.5px;
`;

const ExpandableContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 4rem 2rem;
`;
const ExpandableElement = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: ${(props) => props.marginleft};
`;

const ElementTitle = styled.div`
  font-weight: 600;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const ElementData = styled.div`
  margin-left: 4px;
  font-weight: 400;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const NoData = styled.p`
  font-size: 14px;
  font-weight: 600;
  margin-top: 20px;
  opacity: 1;
`;


const DialogContent = withStyles((theme) => ({
  root: {
    padding: '16px',
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: '8px',
  },
}))(MuiDialogActions);

const useStyles = makeStyles(styles);

const RTMeters = () => {
  const permis = JSON.parse(localStorage.getItem("permission")) || [];
  const markets_admin = permis.includes("markets_admin");
  const markets_update = permis.includes("markets_update");
  const marketContext = useContext(MarketsContext);
  const { market } = marketContext;
  const [open, setOpen] = useState(false);
  const [currentMeter, setCurrentMeter] = useState(null);
  const { gridState, refetch, setMarketmeters } = useMetersTable();

  let dataMembers = gridState.data;

  const classes = useStyles();

  const columns = [
    {
      name: "Numéro de compteur",
      selector: (row) => row.parameters.meternumber,
      sortable: true,
      maxWidth: "9rem",
    },
    {
      name: "Statut dans le marché",
      sortable: true,
      maxWidth: "12rem",
      cell: (row) => row.exist && row.parameters.status ?
        <div style={{ color: metersStatusData.find((x) => x.id == row.parameters.status).color, fontWeight: "bold" }}>
          {metersStatusData.find((x) => x.id == row.parameters.status).name}
        </div> :
        <div style={{ color: "#C53F3F", fontWeight: "bold" }}>
          {'Compteur supprimé'}
        </div>
    },
    {
      name: "Date d'entrée",
      sortable: true,
      maxWidth: "10rem",
      cell: (row) =>
        row.parameters.datebegin && moment(row.parameters.datebegin).isValid()
          ? moment(row.parameters.datebegin).format("DD/MM/YYYY")
          : "",
    },
    {
      name: "Date de sortie",
      sortable: true,
      maxWidth: "10rem",
      cell: (row) =>
        row.parameters.dateend && moment(row.parameters.dateend).isValid()
          ? moment(row.parameters.dateend).format("DD/MM/YYYY")
          : "",
    },
    {
      name: "Description",
      selector: (row) => row.parameters.description,
      sortable: true,
    },
    {
      name: "N° Adhérent au marché",
      selector: (row) => row.marketmembershipnumber,
      sortable: true,
      maxWidth: "13rem",
    },
    {
      name: "Nom d'adhérent",
      selector: (row) => row.marketmembershipname,
      sortable: true,
      maxWidth: "13rem",
    },
    {
      name: "Segment",
      selector: (row) => row.segment,
      sortable: true,
      omit: market.parameters?.general?.energy !== 'electricity',
      maxWidth: "8rem",
    },
    {
      name: "Profil",
      selector: (row) => row.profil,
      sortable: true,
      omit: market.parameters?.general?.energy !== 'gas',
      maxWidth: "8rem",
    },
    {
      name: "Acheminement",
      selector: (row) => row.acheminement,
      sortable: true,
      maxWidth: "10rem",
    },
    {
      name: "",
      sortable: false,
      width: "10rem",
      // eslint-disable-next-line react/button-has-type
      // eslint-disable-next-line react/display-name
      cell: (row) => (
        <>
          <div className="actions-right">
            {markets_update && (
              <>
                <Tooltip title="Editer le compteur">
                  <Button
                    onClick={editMember(row)}
                    color={"warning"}
                    className={classes.actionButton}
                  >
                    <EditIcon fontSize="large" />
                  </Button>
                </Tooltip>

                <Tooltip title="Supprimer le compteur">
                  <CustomDeleteButton
                    deleteTitle={"Suppression du compteur"}
                    deleteSubTitle={`Êtes-vous sûr de vouloir supprimer le compteur ${row.parameters.meternumber}?`}
                    handleSupprimer={deleteMarketMetersFunction(row)}
                  />
                </Tooltip>
              </>
            )}
          </div>
        </>
      ),
    },
  ];

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleEditClose = () => {
    setOpen(false);
  };

  const editMember = (row) => async () => {
    let obj = dataMembers.find((o) => o.id === row.id);
    if (obj) {
      setCurrentMeter(obj);
      handleClickOpen();
    }
  }

  const deleteMarketMetersFunction = (row) => async () => {
    let obj = dataMembers.find((o) => o.id === row.id);
    let idSelected = obj.id;
    let selected = [];
    selected.push(idSelected);
    let url = putMarketMeters("delete");
    axios
      .put(url,
        {
          meters: selected,
        },
        {
          headers: {
            Authorization: `Bearer ${UserService.getToken()}`,
            'Content-Type': "application/json; charset=utf-8",
          },
        })
      .then((res) => {
        if (res.status == 200) {
          setNotification(
            "success",
            `Le compteur a été supprimé`
          );
          refetch();
        } else setNotification("error", "Une erreur est survenue");
      })
      .catch((error) => {
        setNotification("error", "Une erreur est survenue");
      });
  };

  const setNotification = (status, text) => {
    if (status === "success") {
      NotificationManager.success(text);
    } else {
      NotificationManager.error(text);
    }
  };

  const handleSelectedRowsChange = (selection) => {
    setMarketmeters(selection.selectedRows);
  };


  return (
    <>
      <DataTable
        paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
        noDataComponent={<NoData>Aucun compteur à afficher</NoData>}
        noHeader={true}
        columns={columns}
        persistTableHead
        pagination
        subHeader
        onSelectedRowsChange={handleSelectedRowsChange}
        selectableRows={true}
        progressPending={gridState.loading}
        progressComponent={<LinearProgress />}
        expandableRowDisabled={(row) => row.disabled}
        paginationComponentOptions={{
          noRowsPerPage: false,
          rowsPerPageText: "Lignes par page",
          rangeSeparatorText: "sur",
          selectAllRowsItem: false,
          selectAllRowsItemText: "Selectionner toutes les lignes",
        }}
        {...gridState}
      />
      <Dialog
        onClose={handleEditClose}
        aria-labelledby="edituser-dialog-title"
        fullWidth="true"
        maxWidth="sm"
        open={open}
      >
        <CustomDialogTitle id="customized-dialog-title" onClose={handleEditClose}>
          Modification d'un compteur
        </CustomDialogTitle>
        <DialogContent dividers>
          <EditMeterDialog
            marketmeter={currentMeter}
            closeModal={handleEditClose}
            refetch={refetch}
          />
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
    </>
  );
};

export default RTMeters;
